"use client";

import { fetchWorkflowById, fetchWorkflowsPage, type WorkflowFilters } from "@/app/(v2)/workflows/_hooks/workflowsApi";
import { useConnectorsStore, type Connector, type ConnectorType } from "@/stores/connectorsStore";
import { useOrdersStore, type Order } from "@/stores/ordersStore";
import { useTemplatesStore, type Template } from "@/stores/templatesStore";
import { useWalletsStore } from "@/stores/walletsStore";
import { useWorkflowRunsStore, type WorkflowRun } from "@/stores/workflowRunsStore";
import type { Workflow, WorkflowNode } from "@/stores/workflowsStore";
import type { OrganizationInvite, OrganizationMember } from "@/types/organization";
import type { OrganizationWallet } from "@/types/wallet";
import { z } from "zod";
import {
  compactConnector,
  compactConnectorType,
  compactNode,
  compactOrder,
  compactOrganizationInvite,
  compactOrganizationMember,
  compactTemplate,
  compactWallet,
  compactWorkflow,
  compactRun,
} from "./voiceCompact";
import {
  DEFAULT_TOOL_ITEMS,
  MAX_TOOL_ITEMS,
  type Resolution,
  type VoiceTemplateCategory,
  workflowStatusSchema,
} from "./voiceToolTypes";

export function clampLimit(limit: number | undefined, fallback = DEFAULT_TOOL_ITEMS) {
  if (!Number.isFinite(limit ?? NaN)) return fallback;
  return Math.min(MAX_TOOL_ITEMS, Math.max(1, Math.floor(limit ?? fallback)));
}

export function normalizeText(value: unknown) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

export function includesQuery(values: unknown[], query: string | undefined) {
  const needle = normalizeText(query);
  if (!needle) return true;
  return values.some(value => normalizeText(value).includes(needle));
}

export function getCurrentPathname() {
  if (typeof window === "undefined") return "";
  return window.location.pathname;
}

export function getCurrentSearchParams() {
  if (typeof window === "undefined") return new URLSearchParams();
  return new URLSearchParams(window.location.search);
}

export function getCurrentWorkflowId() {
  const params = getCurrentSearchParams();
  return params.get("id") || params.get("draft") || undefined;
}

export async function searchWorkflows(params: {
  limit?: number;
  query?: string;
  status?: z.infer<typeof workflowStatusSchema>;
}) {
  const filters: WorkflowFilters = {
    owner: "",
    sort: "updatedAt:desc",
    ...(params.query ? { q: params.query } : {}),
    ...(params.status ? { status: params.status } : {}),
  };

  const page = await fetchWorkflowsPage({
    ...filters,
    page: 0,
    pageSize: clampLimit(params.limit, DEFAULT_TOOL_ITEMS),
  });

  return page.items ?? [];
}

export async function resolveWorkflow(identifier?: string): Promise<Resolution<Workflow>> {
  const currentWorkflowId = getCurrentWorkflowId();
  const rawIdentifier = identifier?.trim();
  const query = rawIdentifier || currentWorkflowId;

  if (!query) {
    return { ok: false, reason: "No workflow was specified and no workflow is open." };
  }

  try {
    const byId = await fetchWorkflowById(query);
    return { ok: true, item: byId };
  } catch {
    // Fall through to name search.
  }

  const matches = await searchWorkflows({ query, limit: 10 });
  const exact = matches.find(workflow => {
    const normalized = normalizeText(query);
    return normalizeText(workflow.id) === normalized || normalizeText(workflow.name) === normalized;
  });

  if (exact) {
    return { ok: true, item: exact.definition ? exact : await fetchWorkflowById(exact.id) };
  }

  if (matches.length === 1) {
    const workflow = matches[0];
    return { ok: true, item: workflow.definition ? workflow : await fetchWorkflowById(workflow.id) };
  }

  if (matches.length > 1) {
    return {
      ok: false,
      reason: `Multiple workflows matched "${query}". Ask the user which one to use.`,
      matches: matches.slice(0, 5).map(compactWorkflow),
    };
  }

  return { ok: false, reason: `No workflow matched "${query}".` };
}

export async function ensureConnectorStoresLoaded() {
  const store = useConnectorsStore.getState();
  await Promise.all([store.loadConnectorTypes(), store.loadConnectors()]);
  return useConnectorsStore.getState();
}

export async function resolveConnector(identifier: string): Promise<Resolution<Connector>> {
  const store = await ensureConnectorStoresLoaded();
  const query = identifier.trim();
  const matches = store.connectors.filter(connector =>
    includesQuery([connector.id, connector.name, connector.type, connector.description], query),
  );
  const exact = matches.find(connector => {
    const normalized = normalizeText(query);
    return (
      normalizeText(connector.id) === normalized ||
      normalizeText(connector.name) === normalized ||
      normalizeText(connector.type) === normalized
    );
  });

  if (exact) return { ok: true, item: exact };
  if (matches.length === 1) return { ok: true, item: matches[0] };
  if (matches.length > 1) {
    return {
      ok: false,
      reason: `Multiple connectors matched "${query}".`,
      matches: matches.slice(0, 5).map(compactConnector),
    };
  }
  return { ok: false, reason: `No connector matched "${query}".` };
}

export async function resolveConnectorType(identifier: string): Promise<Resolution<ConnectorType>> {
  const store = await ensureConnectorStoresLoaded();
  const query = identifier.trim();
  const matches = store.connectorTypes.filter(connectorType =>
    includesQuery([connectorType.type, connectorType.name, connectorType.description, connectorType.provider], query),
  );
  const exact = matches.find(connectorType => {
    const normalized = normalizeText(query);
    return normalizeText(connectorType.type) === normalized || normalizeText(connectorType.name) === normalized;
  });

  if (exact) return { ok: true, item: exact };
  if (matches.length === 1) return { ok: true, item: matches[0] };
  if (matches.length > 1) {
    return {
      ok: false,
      reason: `Multiple connector types matched "${query}".`,
      matches: matches.slice(0, 5).map(compactConnectorType),
    };
  }
  return { ok: false, reason: `No connector type matched "${query}".` };
}

export async function loadTemplates(category: VoiceTemplateCategory = "all", tags?: string[]) {
  const store = useTemplatesStore.getState();
  await store.loadTemplatesByCategory(category, tags);
  return useTemplatesStore.getState().templates;
}

export async function resolveTemplate(
  identifier: string,
  category: VoiceTemplateCategory = "all",
): Promise<Resolution<Template>> {
  const query = identifier.trim();
  const templates = await loadTemplates(category);
  const matches = templates.filter(template =>
    includesQuery(
      [
        template.id,
        template.slug,
        template.name,
        template.description,
        ...(template.tags ?? []).flatMap(tag => [tag.name, tag.slug]),
      ],
      query,
    ),
  );
  const exact = matches.find(template => {
    const normalized = normalizeText(query);
    return (
      normalizeText(template.id) === normalized ||
      normalizeText(template.slug) === normalized ||
      normalizeText(template.name) === normalized
    );
  });

  if (exact) return { ok: true, item: exact };
  if (matches.length === 1) return { ok: true, item: matches[0] };
  if (matches.length > 1) {
    return {
      ok: false,
      reason: `Multiple templates matched "${query}".`,
      matches: matches.slice(0, 5).map(compactTemplate),
    };
  }
  return { ok: false, reason: `No template matched "${query}".` };
}

export async function resolveRun(identifier?: string, workflowId?: string): Promise<Resolution<WorkflowRun>> {
  const store = useWorkflowRunsStore.getState();
  const query = identifier?.trim();

  if (query && !["latest", "recent", "last"].includes(normalizeText(query))) {
    try {
      const run = await store.getRunById(query);
      return { ok: true, item: run };
    } catch {
      // Fall through to cached/page search.
    }
  }

  await store.loadRunsPage(0, workflowId, undefined);
  const runs = useWorkflowRunsStore.getState().runs;

  if (!query || ["latest", "recent", "last"].includes(normalizeText(query))) {
    const latest = runs[0];
    return latest ? { ok: true, item: latest } : { ok: false, reason: "No recent workflow runs were found." };
  }

  const matches = runs.filter(run => includesQuery([run.id, run.workflowId, run.status, run.triggerSource], query));
  if (matches.length === 1) return { ok: true, item: matches[0] };
  if (matches.length > 1) {
    return {
      ok: false,
      reason: `Multiple workflow runs matched "${query}".`,
      matches: matches.slice(0, 5).map(compactRun),
    };
  }

  return { ok: false, reason: `No workflow run matched "${query}".` };
}

export async function resolveOrder(identifier: string): Promise<Resolution<Order>> {
  const store = useOrdersStore.getState();
  if (store.orders.length === 0) {
    await store.fetchOrders();
  }

  const query = identifier.trim();
  const orders = useOrdersStore.getState().orders;
  const matches = orders.filter(order =>
    includesQuery([order.id, order.label, order.assetAddress, order.status, order.orderType], query),
  );
  const exact = matches.find(
    order => normalizeText(order.id) === normalizeText(query) || normalizeText(order.label) === normalizeText(query),
  );

  if (exact) return { ok: true, item: exact };
  if (matches.length === 1) return { ok: true, item: matches[0] };
  if (matches.length > 1) {
    return {
      ok: false,
      reason: `Multiple orders matched "${query}".`,
      matches: matches.slice(0, 5).map(compactOrder),
    };
  }
  return { ok: false, reason: `No order matched "${query}".` };
}

export async function resolveWallet(identifier?: string): Promise<Resolution<OrganizationWallet>> {
  await useWalletsStore.getState().loadWallets();
  const wallets = useWalletsStore.getState().wallets;
  const query = identifier?.trim();

  if (!query) {
    const defaultWallet = wallets.find(wallet => wallet.isDefault) ?? wallets[0];
    return defaultWallet ? { ok: true, item: defaultWallet } : { ok: false, reason: "No organization wallets found." };
  }

  const matches = wallets.filter(wallet =>
    includesQuery([wallet.id, wallet.name, wallet.address, wallet.providerType, wallet.accountType], query),
  );
  const exact = matches.find(wallet => {
    const normalized = normalizeText(query);
    return (
      normalizeText(wallet.id) === normalized ||
      normalizeText(wallet.name) === normalized ||
      normalizeText(wallet.address) === normalized
    );
  });

  if (exact) return { ok: true, item: exact };
  if (matches.length === 1) return { ok: true, item: matches[0] };
  if (matches.length > 1) {
    return {
      ok: false,
      reason: `Multiple wallets matched "${query}".`,
      matches: matches.slice(0, 5).map(compactWallet),
    };
  }

  return { ok: false, reason: `No wallet matched "${query}".` };
}

export function resolveWorkflowNode(
  workflow: Workflow,
  identifier: string,
): Resolution<{ id: string; node: WorkflowNode }> {
  const query = identifier.trim();
  const entries = Object.entries(workflow.definition?.nodes ?? {});
  const matches = entries.filter(([nodeId, workflowNode]) =>
    includesQuery(
      [
        nodeId,
        workflowNode.type,
        workflowNode.titleOverride,
        workflowNode.description,
        workflowNode.connector?.id,
        workflowNode.connector?.type,
        ...Object.keys(workflowNode.payload ?? {}),
      ],
      query,
    ),
  );
  const exact = matches.find(([nodeId, workflowNode]) => {
    const normalized = normalizeText(query);
    return normalizeText(nodeId) === normalized || normalizeText(workflowNode.titleOverride) === normalized;
  });
  const match = exact ?? (matches.length === 1 ? matches[0] : null);

  if (match) return { ok: true, item: { id: match[0], node: match[1] } };

  return {
    ok: false,
    reason: matches.length > 1 ? "Multiple nodes matched. Ask the user which node to use." : "No node matched.",
    matches: matches.slice(0, 6).map(([nodeId, workflowNode]) => compactNode(nodeId, workflowNode)),
  };
}

export function resolveOrganizationMember(
  members: OrganizationMember[],
  identifier: string,
): Resolution<OrganizationMember> {
  const query = identifier.trim();
  const matches = members.filter(member =>
    includesQuery([member.id, member.userId, member.userEmail, member.userName, member.role], query),
  );
  const exact = matches.find(member => {
    const normalized = normalizeText(query);
    return (
      normalizeText(member.id) === normalized ||
      normalizeText(member.userId) === normalized ||
      normalizeText(member.userEmail) === normalized
    );
  });

  if (exact) return { ok: true, item: exact };
  if (matches.length === 1) return { ok: true, item: matches[0] };
  if (matches.length > 1) {
    return {
      ok: false,
      reason: `Multiple members matched "${query}".`,
      matches: matches.slice(0, 5).map(compactOrganizationMember),
    };
  }
  return { ok: false, reason: `No member matched "${query}".` };
}

export function resolveOrganizationInvite(
  invites: OrganizationInvite[],
  identifier: string,
): Resolution<OrganizationInvite> {
  const query = identifier.trim();
  const matches = invites.filter(invite =>
    includesQuery([invite.id, invite.inviteeEmail, invite.inviteeUserId, invite.role, invite.status], query),
  );
  const exact = matches.find(invite => {
    const normalized = normalizeText(query);
    return normalizeText(invite.id) === normalized || normalizeText(invite.inviteeEmail) === normalized;
  });

  if (exact) return { ok: true, item: exact };
  if (matches.length === 1) return { ok: true, item: matches[0] };
  if (matches.length > 1) {
    return {
      ok: false,
      reason: `Multiple invites matched "${query}".`,
      matches: matches.slice(0, 5).map(compactOrganizationInvite),
    };
  }
  return { ok: false, reason: `No invite matched "${query}".` };
}

export function getTemplateMissingConfig(template: Template) {
  const missingRequiredProps =
    template.templateProps?.filter(prop => prop.required && prop.default === undefined).map(prop => prop.key) ?? [];
  const connectorTypes = new Set<string>();

  for (const node of Object.values(template.definition?.nodes ?? {})) {
    if (node.connector?.type && !node.connector.id && !node.connector.type.includes("{{")) {
      connectorTypes.add(node.connector.type);
    }
  }

  return {
    missingRequiredProps,
    missingConnectorTypes: [...connectorTypes],
  };
}
